import { fromDate, toCalendarDate } from "@internationalized/date";
import { useTranslations } from "next-intl";
import { use, useState } from "react";
import { useDateFormatter } from "react-aria";

import { CONTACT_EMAIL } from "@trip/constants";

import { AngleUpSmallIcon } from "~/components/icons/angle-up-small";
import { Large } from "~/components/typography";
import { graphql } from "~/lib/dato-graphql";
import { useCurrencyFormatter } from "~/lib/use-currency";
import { useDateRangeFormatter } from "~/lib/use-date-range-formatter";
import { useDatoQuery } from "~/lib/use-urql-queries";

import { TripContext } from "../_lib/trip-context";
import {
  PassengersAndDates,
  type TripMutationParams,
} from "./passengers-and-dates";

const CONTRIBUTOR_QUERY = graphql(`
  query Contributor($email: String!) {
    contributor(filter: { emailAddress: { eq: $email } }) {
      name
      emailAddress
    }
  }
`);

export function CartSettings({
  onChangeParams,
  isLoading,
}: {
  isLoading: boolean;
  onChangeParams: (params: TripMutationParams) => void;
}) {
  const t = useTranslations("TripDetails");
  const trip = use(TripContext);
  if (!trip) {
    throw new Error("TripContext is not available");
  }
  const { date, nights, total, passengers, cartLink, proposal } = trip;
  const [showDetails, setShowDetails] = useState(false);

  const formatCurrency = useCurrencyFormatter();
  const formatDate = useDateFormatter({ dateStyle: "medium" });
  const formatDateRange = useDateRangeFormatter({ timeZone: "UTC" });

  const start = toCalendarDate(fromDate(date, "UTC"));
  const end = start.add({ days: nights });

  const [contributorQuery] = useDatoQuery({
    query: CONTRIBUTOR_QUERY,
    variables: { email: cartLink?.createdBy.email ?? "" },
    pause: !cartLink?.createdBy.email,
  });

  if (proposal.hasProposal && !proposal.isExpired) {
    return (
      <>
        <Large className="mb-0.5 font-medium">
          <span>{formatCurrency(total)}</span>
        </Large>
        <div className="mb-2 text-sm text-neutral-500">
          {t("dateForPassengers", {
            dateRange: formatDateRange({ start, end }),
            adults: passengers.adults,
            children: passengers.children.length,
          })}
        </div>
        <div className="text-sm">
          {t.rich("changeProposalDetails", {
            contactUs: (chunks) => (
              <a
                className="text-prune-500 font-medium"
                href={`mailto:${contributorQuery.data?.contributor?.emailAddress ?? CONTACT_EMAIL}`}
              >
                {chunks}
              </a>
            ),
          })}{" "}
          {proposal.expires ? (
            <>
              {t("proposalExpires", {
                date: formatDate.format(proposal.expires),
              })}
            </>
          ) : null}
        </div>
      </>
    );
  }

  return (
    <>
      <Large className="mb-0.5 font-medium">
        <span>{formatCurrency(total)}</span>
      </Large>
      {showDetails ? null : (
        <div>
          <div className="mb-2 text-sm text-neutral-500">
            {t("dateForPassengers", {
              dateRange: formatDateRange({ start, end }),
              adults: passengers.adults,
              children: passengers.children.length,
            })}
          </div>
        </div>
      )}
      <div>
        <button
          type="button"
          className="text-prune-500 flex items-center gap-1 text-sm font-medium"
          onClick={(event) => {
            event.preventDefault();
            setShowDetails((showDetails) => !showDetails);
          }}
        >
          {showDetails ? t("hideDetails") : t("changeDateAndPassengers")}{" "}
          <AngleUpSmallIcon className={showDetails ? "" : "-rotate-180"} />
        </button>
        {showDetails ? (
          <div>
            <PassengersAndDates
              isLoading={isLoading}
              onChangeParams={onChangeParams}
            />
          </div>
        ) : null}
      </div>
    </>
  );
}
